/**
 * Capacity what-if for NXEdge Morgan Hill — the pure model.
 *
 * Load per machine group per ISO week comes from Vishal's Q4 factory execution
 * plan and the routings loaded as labor standards (Upper Liners and Wagon Wheel
 * from Vishal, Lower Liners and Plasma Screens assumed). Capacity is EnPro's
 * standard time model: one operator gives 29.6 productive hours a week at no
 * overtime, 37.6 at 20%, 43.1 at 40%, 47.1 at 50%; a second shift adds
 * another 29.6 per machine. The assumption set is embedded here rather than
 * read from the API so the page cannot come up empty on the day.
 *
 * Detailing and CMM inspection are stations every part passes through after
 * machining (site visit, 3 Nov 2026: inspection is the plant's bottleneck and
 * detailing is short of people). Their shortfall is hours across all parts,
 * not one family's units.
 */
export type FamilyKey = "UL" | "LL" | "PS" | "WW"
export type GroupId = "ROUGH" | "MC3" | "MC4" | "MC5" | "MC6" | "PLASMA" | "PSFIN" | "WW" | "DETAIL" | "CMM" | "ANODIZE" | "THERMAL" | "FLUORO" | "MRB"
/** The two NXEdge sites the plan crosses: machining at Morgan Hill, coating at Boise. */
export type SiteKey = "morgan-hill" | "boise"
export const SITES: { key: SiteKey; label: string; short: string }[] = [
	{ key: "morgan-hill", label: "NXEdge · Morgan Hill, CA", short: "Morgan Hill" },
	{ key: "boise", label: "NXEdge · Boise, ID", short: "Boise" },
];
export type OvertimeLevel = 0 | 20 | 40 | 50

export const WEEKS = [45, 46, 47, 48, 49, 50, 51, 52];
export const WEEK_MONDAYS: Record<number, string> = {
	45: "2026-11-02", 46: "2026-11-09", 47: "2026-11-16", 48: "2026-11-23",
	49: "2026-11-30", 50: "2026-12-07", 51: "2026-12-14", 52: "2026-12-21",
};

export interface Family {
	key: FamilyKey;
	name: string;
	/** Weekly plan quantity by ISO week (Vishal's sheet). */
	plan: Record<number, number>;
}

export const FAMILIES: Family[] = [
	{ key: "UL", name: "Upper Liners", plan: { 45: 24, 46: 24, 47: 24, 48: 24, 49: 26, 50: 26, 51: 26, 52: 26 } },
	{ key: "LL", name: "Lower Liners", plan: { 45: 18, 46: 18, 47: 18, 48: 18, 49: 20, 50: 20, 51: 20, 52: 20 } },
	{ key: "PS", name: "Plasma Screens", plan: { 45: 50, 46: 50, 47: 50, 48: 50, 49: 50, 50: 50, 51: 50, 52: 50 } },
	{ key: "WW", name: "Wagon Wheel", plan: { 45: 75, 46: 75, 47: 75, 48: 75, 49: 75, 50: 75, 51: 75, 52: 75 } },
];

export interface Group {
	id: GroupId;
	name: string;
	short: string;
	machines: number;
	/** Operators whose home is this group today. */
	people: number;
	/** Fixture ops: the batch size lever applies. */
	batchable: boolean;
	/** The family whose units the "short" column counts. */
	countsFamily: FamilyKey;
	/** Every family passes through: the shortfall is hours, not one family's units. */
	shared: boolean;
	/** What one capacity slot is called, and who runs it. */
	unit: string;
	units: string;
	role: string;
	/** Where the station is. */
	site: SiteKey;
}

const MACHINE = { shared: false, unit: "machine", units: "machines", role: "operator", site: "morgan-hill" as SiteKey };

export const GROUPS: Group[] = [
	{ id: "ROUGH", name: "MC-1 / MC-2 · Rough machining", short: "MC-1/2", machines: 2, people: 3, batchable: false, countsFamily: "UL", ...MACHINE },
	{ id: "MC3", name: "MC-3 · Upper Liner finish", short: "MC-3", machines: 1, people: 1, batchable: true, countsFamily: "UL", ...MACHINE },
	{ id: "MC4", name: "MC-4 · Upper Liner bore", short: "MC-4", machines: 1, people: 1, batchable: true, countsFamily: "UL", ...MACHINE },
	{ id: "MC5", name: "MC-5 · Lower Liner finish", short: "MC-5", machines: 1, people: 1, batchable: true, countsFamily: "LL", ...MACHINE },
	{ id: "MC6", name: "MC-6 · Lower Liner bore", short: "MC-6", machines: 1, people: 1, batchable: true, countsFamily: "LL", ...MACHINE },
	{ id: "PLASMA", name: "PS-1 / PS-2 / PS-3 · Plasma machining", short: "PS-1/2/3", machines: 3, people: 3, batchable: false, countsFamily: "PS", ...MACHINE },
	{ id: "PSFIN", name: "PS Finish 1 / 2 · Slot finish", short: "PS Finish", machines: 2, people: 2, batchable: false, countsFamily: "PS", ...MACHINE },
	{ id: "WW", name: "WW-1 / WW-2 / WW-3 · Wagon Wheel", short: "WW-1/2/3", machines: 3, people: 3, batchable: false, countsFamily: "WW", ...MACHINE },
	{ id: "DETAIL", name: "Detailing 1 / 2 / 3 · Deburr & hand finish", short: "Detailing", machines: 3, people: 2, batchable: false, countsFamily: "UL", shared: true, unit: "bench", units: "benches", role: "detailer", site: "morgan-hill" },
	{ id: "CMM", name: "CMM-1 / CMM-2 · Inspection", short: "CMM", machines: 2, people: 2, batchable: false, countsFamily: "UL", shared: true, unit: "CMM", units: "CMMs", role: "inspector", site: "morgan-hill" },
	// Material review: the team that works customer returns, internal issues and nonconformances — its own case load, not a step every lot takes
	{ id: "MRB", name: "MRB · Material review", short: "MRB", machines: 1, people: 1, batchable: false, countsFamily: "UL", shared: true, unit: "bench", units: "benches", role: "quality engineer", site: "morgan-hill" },
];

/**
 * The Boise coating lines (Dustin, 4 Nov 2026): three kinds of coating
 * machines that finish Morgan Hill's parts. One line and one technician each
 * is ASSUMED, as is which family goes to which line and the third line's name.
 */
export const BOISE_GROUPS: Group[] = [
	{ id: "ANODIZE", name: "Anodize line 1 · Anodizing", short: "Anodize", machines: 1, people: 1, batchable: false, countsFamily: "UL", shared: false, unit: "line", units: "lines", role: "technician", site: "boise" },
	{ id: "THERMAL", name: "Thermal spray booth 1 · Thermal spray coating", short: "Thermal spray", machines: 1, people: 1, batchable: false, countsFamily: "PS", shared: false, unit: "booth", units: "booths", role: "technician", site: "boise" },
	{ id: "FLUORO", name: "Fluoropolymer line 1 · Fluoropolymer coating", short: "Fluoropolymer", machines: 1, people: 1, batchable: false, countsFamily: "WW", shared: false, unit: "line", units: "lines", role: "technician", site: "boise" },
];
export const ALL_GROUPS: Group[] = [...GROUPS, ...BOISE_GROUPS];
export const groupsOf = (site: SiteKey): Group[] => ALL_GROUPS.filter((g) => g.site === site);

export interface Operation {
	group: GroupId;
	family: FamilyKey;
	name: string;
	setupMin: number;
	runMin: number;
	/** Setup and run are per fixture of `batch` units (Vishal's "8 hours"); else per unit. */
	perBatch: boolean;
	source: "Vishal" | "assumed" | "API";
	/** The step's place in the product's routing, when the source says (the labor standard's sequence). */
	seq?: number;
}

export const OPERATIONS: Operation[] = [
	{ group: "ROUGH", family: "UL", name: "op 1 rough", setupMin: 15, runMin: 45, perBatch: false, source: "Vishal" },
	{ group: "MC3", family: "UL", name: "op 2 finish", setupMin: 30, runMin: 480, perBatch: true, source: "Vishal" },
	{ group: "MC4", family: "UL", name: "op 3 bore", setupMin: 30, runMin: 360, perBatch: true, source: "Vishal" },
	{ group: "ROUGH", family: "LL", name: "op 1 rough", setupMin: 15, runMin: 30, perBatch: false, source: "assumed" },
	{ group: "MC5", family: "LL", name: "op 2 finish", setupMin: 30, runMin: 300, perBatch: true, source: "assumed" },
	{ group: "MC6", family: "LL", name: "op 3 bore", setupMin: 30, runMin: 240, perBatch: true, source: "assumed" },
	{ group: "PLASMA", family: "PS", name: "machining", setupMin: 20, runMin: 120, perBatch: false, source: "assumed" },
	{ group: "PSFIN", family: "PS", name: "slot finish", setupMin: 0, runMin: 45, perBatch: false, source: "assumed" },
	{ group: "WW", family: "WW", name: "machining", setupMin: 10, runMin: 60, perBatch: false, source: "Vishal" },
	// Detailing: deburr, edge break, hand finish, clean and mark. Site visit: takes a lot of time, short of people.
	{ group: "DETAIL", family: "UL", name: "detailing", setupMin: 0, runMin: 60, perBatch: false, source: "assumed" },
	{ group: "DETAIL", family: "LL", name: "detailing", setupMin: 0, runMin: 45, perBatch: false, source: "assumed" },
	{ group: "DETAIL", family: "PS", name: "detailing", setupMin: 0, runMin: 15, perBatch: false, source: "assumed" },
	{ group: "DETAIL", family: "WW", name: "detailing", setupMin: 0, runMin: 12, perBatch: false, source: "assumed" },
	// CMM: load, program run and report per part. Site visit: the plant's bottleneck; machinists wait on it.
	{ group: "CMM", family: "UL", name: "CMM inspection", setupMin: 10, runMin: 90, perBatch: false, source: "assumed" },
	{ group: "CMM", family: "LL", name: "CMM inspection", setupMin: 10, runMin: 60, perBatch: false, source: "assumed" },
	{ group: "CMM", family: "PS", name: "CMM inspection", setupMin: 5, runMin: 10, perBatch: false, source: "assumed" },
	{ group: "CMM", family: "WW", name: "CMM inspection", setupMin: 2, runMin: 8, perBatch: false, source: "assumed" },
	{ group: "ANODIZE", family: "UL", name: "anodize", setupMin: 0, runMin: 45, perBatch: false, source: "assumed" },
	{ group: "ANODIZE", family: "LL", name: "anodize", setupMin: 0, runMin: 36, perBatch: false, source: "assumed" },
	{ group: "THERMAL", family: "PS", name: "thermal spray coating", setupMin: 0, runMin: 40, perBatch: false, source: "assumed" },
	{ group: "FLUORO", family: "WW", name: "fluoropolymer coating", setupMin: 0, runMin: 24, perBatch: false, source: "assumed" },
];

/**
 * Hours a week a station carries that do not come from the products' units:
 * the MRB team's cases (the try site's standards: an NCR 90 min, a DMR 60, an
 * RMA 240; about 6 NCRs, 3 DMRs and 1 RMA a week ASSUMED).
 */
export const STANDING_LOAD: Partial<Record<GroupId, number>> = { MRB: 16 };

export const DEFAULT_BATCH = 5;
export const OT_LEVELS: OvertimeLevel[] = [0, 20, 40, 50];
/** EnPro standard time model: productive hours per person-week at each overtime level. */
export const HOURS_AT_OT: Record<OvertimeLevel, number> = { 0: 29.6, 20: 37.6, 40: 43.1, 50: 47.1 };
export const SECOND_SHIFT_HOURS = 29.6;

export interface Person {
	id: string;
	name: string;
	home: GroupId;
	/** Level 1–3 on a group; absent = not trained. */
	skills: Partial<Record<GroupId, number>>;
}

/** The 19 people on the roster, with the skills matrix the roster uses. */
export const PEOPLE: Person[] = [
	{ id: "NXE-01", name: "Tomas Herrera", home: "ROUGH", skills: { ROUGH: 3, MC3: 2 } },
	{ id: "NXE-02", name: "Dale Whitcomb", home: "ROUGH", skills: { ROUGH: 3, MC5: 2, DETAIL: 2 } },
	{ id: "NXE-03", name: "Ray Kowalczyk", home: "MC3", skills: { MC3: 3, MC4: 2 } },
	{ id: "NXE-04", name: "Priya Nair", home: "MC4", skills: { MC4: 2, MC3: 1, CMM: 1 } },
	{ id: "NXE-05", name: "Marcus Lindqvist", home: "MC5", skills: { MC5: 2, MC6: 1 } },
	{ id: "NXE-06", name: "Jenna Okafor", home: "MC6", skills: { MC6: 2, MC5: 1 } },
	{ id: "NXE-07", name: "Hector Villanueva", home: "PLASMA", skills: { PLASMA: 3, ROUGH: 2 } },
	{ id: "NXE-08", name: "Sam Tanaka", home: "PLASMA", skills: { PLASMA: 2, PSFIN: 1 } },
	{ id: "NXE-09", name: "Alicia Brennan", home: "PLASMA", skills: { PLASMA: 1 } },
	{ id: "NXE-10", name: "Rosa Delgado", home: "PSFIN", skills: { PSFIN: 2, PLASMA: 1, CMM: 1 } },
	{ id: "NXE-11", name: "Kevin Marsh", home: "PSFIN", skills: { PSFIN: 2, WW: 1, DETAIL: 1 } },
	{ id: "NXE-12", name: "Omar Haddad", home: "WW", skills: { WW: 2, ROUGH: 1 } },
	{ id: "NXE-13", name: "Grace Pham", home: "WW", skills: { WW: 2, PSFIN: 1, DETAIL: 1 } },
	{ id: "NXE-14", name: "Tyler Brooks", home: "WW", skills: { WW: 1 } },
	{ id: "NXE-15", name: "Elena Vasquez", home: "ROUGH", skills: { ROUGH: 3, MC3: 3, MC4: 3 } },
	{ id: "NXE-16", name: "Nadia Petrova", home: "DETAIL", skills: { DETAIL: 3 } },
	{ id: "NXE-17", name: "Carlos Mendez", home: "DETAIL", skills: { DETAIL: 2, WW: 1 } },
	{ id: "NXE-18", name: "Linh Tran", home: "CMM", skills: { CMM: 3, DETAIL: 1 } },
	{ id: "NXE-19", name: "Victor Osei", home: "CMM", skills: { CMM: 2 } },
	{ id: "NXE-20", name: "Renee Alvarez", home: "MRB", skills: { MRB: 3, CMM: 1 } },
	// Boise (assumed): one technician per coating line
	{ id: "NXB-01", name: "Marisol Ortega", home: "ANODIZE", skills: { ANODIZE: 3, FLUORO: 1 } },
	{ id: "NXB-02", name: "Derek Hale", home: "THERMAL", skills: { THERMAL: 3, ANODIZE: 1 } },
	{ id: "NXB-03", name: "Aiden Walsh", home: "FLUORO", skills: { FLUORO: 2, THERMAL: 1 } },
];

export interface Move {
	personId: string;
	to: GroupId;
}

export interface Levers {
	overtime: Record<GroupId, OvertimeLevel>;
	/** Machines running a second shift (another 29.6 h each). */
	secondShift: Record<GroupId, number>;
	/** Machines added, each with an operator. */
	extraMachines: Record<GroupId, number>;
	/** People hired to staff slots that have nobody today. */
	extraPeople: Record<GroupId, number>;
	batch: Record<GroupId, number>;
	moves: Move[];
}

const byGroup = <T,>(value: T): Record<GroupId, T> =>
	Object.fromEntries(ALL_GROUPS.map((g) => [g.id, value])) as Record<GroupId, T>;

export const defaultLevers = (): Levers => ({
	overtime: byGroup<OvertimeLevel>(0),
	secondShift: byGroup(0),
	extraMachines: byGroup(0),
	extraPeople: byGroup(0),
	batch: byGroup(DEFAULT_BATCH),
	moves: [],
});

export const familyOf = (key: FamilyKey): Family => FAMILIES.find((f) => f.key === key) as Family;
export const groupOf = (id: GroupId): Group => ALL_GROUPS.find((g) => g.id === id) as Group;

/** Hours one unit takes on an operation, setup amortised over the fixture. */
export const unitHours = (op: Operation, batch: number): number =>
	op.perBatch ? (op.setupMin + op.runMin) / Math.max(1, batch) / 60 : (op.setupMin + op.runMin) / 60;

/** Where each person is after the moves. */
export function placements(levers: Levers): Map<string, GroupId> {
	const where = new Map(PEOPLE.map((p) => [p.id, p.home]));
	levers.moves.forEach((m) => where.set(m.personId, m.to));
	return where;
}

export const peopleIn = (group: GroupId, levers: Levers): Person[] => {
	const where = placements(levers);
	return PEOPLE.filter((p) => where.get(p.id) === group);
};

/** Groups a person could move to: trained there (level 1 or above) and not already there. */
export const movesFor = (person: Person, levers: Levers): GroupId[] => {
	const here = placements(levers).get(person.id);
	return GROUPS.filter((g) => g.id !== here && (person.skills[g.id] ?? 0) >= 1).map((g) => g.id);
};

export const loadHours = (group: GroupId, week: number, levers: Levers): number =>
	OPERATIONS.filter((op) => op.group === group).reduce(
		(sum, op) => sum + (familyOf(op.family).plan[week] ?? 0) * unitHours(op, levers.batch[group]),
		0
	);

export interface Capacity {
	machines: number;
	operators: number;
	hoursPerOperator: number;
	secondShiftHours: number;
	total: number;
}

export function capacityFor(group: GroupId, levers: Levers): Capacity {
	const g = groupOf(group);
	const machines = g.machines + levers.extraMachines[group];
	// An added machine comes with its operator; a moved-in or hired person needs a free slot.
	const operators = Math.min(machines, peopleIn(group, levers).length + levers.extraMachines[group] + levers.extraPeople[group]);
	const hoursPerOperator = HOURS_AT_OT[levers.overtime[group]];
	const secondShiftHours = Math.min(levers.secondShift[group], machines) * SECOND_SHIFT_HOURS;
	return { machines, operators, hoursPerOperator, secondShiftHours, total: operators * hoursPerOperator + secondShiftHours };
}

/** Slots with nobody on them, after moves and hires. */
export const emptySlots = (group: GroupId, levers: Levers): number => {
	const c = capacityFor(group, levers);
	return Math.max(0, c.machines - c.operators);
};

export interface WeekCell {
	week: number;
	load: number;
	capacity: number;
	/** load / capacity, as a percentage. */
	utilization: number;
	over: boolean;
	/** Hours of work that week the group cannot do. */
	hoursShort: number;
	/** Units of the group's counted family that will not get made that week. */
	unitsShort: number;
}

export interface GroupRow {
	group: Group;
	capacity: Capacity;
	weeks: WeekCell[];
	weeksOver: number;
	/** The worst week's shortfall. */
	unitsShortMax: number;
	hoursShortMax: number;
}

export function rowFor(group: GroupId, levers: Levers): GroupRow {
	const g = groupOf(group);
	const capacity = capacityFor(group, levers);
	const countedOp = OPERATIONS.find((op) => op.group === group && op.family === g.countsFamily) as Operation;
	const perUnit = unitHours(countedOp, levers.batch[group]);
	const weeks = WEEKS.map((week) => {
		const load = loadHours(group, week, levers);
		const over = load > capacity.total + 1e-9;
		return {
			week,
			load,
			capacity: capacity.total,
			utilization: capacity.total > 0 ? Math.round((load / capacity.total) * 100) : 0,
			over,
			hoursShort: over ? load - capacity.total : 0,
			unitsShort: over ? Math.ceil((load - capacity.total) / perUnit - 1e-9) : 0,
		};
	});
	return {
		group: g,
		capacity,
		weeks,
		weeksOver: weeks.filter((w) => w.over).length,
		unitsShortMax: Math.max(0, ...weeks.map((w) => w.unitsShort)),
		hoursShortMax: Math.max(0, ...weeks.map((w) => w.hoursShort)),
	};
}

export const allRows = (levers: Levers): GroupRow[] => GROUPS.map((g) => rowFor(g.id, levers));

/** Wagon Wheel ran 86 units behind plan at the end of WW43 (Vishal's sheet). */
export const WW_BEHIND_UNITS = 86;
export const WW_BEHIND_HOURS = WW_BEHIND_UNITS * unitHours(OPERATIONS.find((op) => op.group === "WW") as Operation, DEFAULT_BATCH);

export interface CatchUpOption {
	label: string;
	capacity: number;
	/** Hours a week left after the 75-a-week plan. */
	surplus: number;
	/** Weeks to clear the backlog; null when it never clears. */
	weeks: number | null;
}

/** How long the Wagon Wheel backlog takes to clear at each overtime level, and with a fourth person. */
export function wagonWheelCatchUp(levers: Levers): CatchUpOption[] {
	const load = loadHours("WW", 45, levers);
	const base = capacityFor("WW", levers);
	const option = (label: string, capacity: number): CatchUpOption => {
		const surplus = capacity - load;
		return { label, capacity, surplus, weeks: surplus > 0.05 ? Math.ceil((WW_BEHIND_HOURS / surplus) * 10) / 10 : null };
	};
	return [
		...OT_LEVELS.map((ot) => option(ot === 0 ? "No overtime" : `${ot}% overtime`, base.operators * HOURS_AT_OT[ot] + base.secondShiftHours)),
		option("A fourth person on a second-shift slot", base.total + SECOND_SHIFT_HOURS),
	];
}

/** The most Wagon Wheels a week the group can make as set. */
export const wagonWheelMaxUnits = (levers: Levers): number =>
	Math.floor(capacityFor("WW", levers).total / unitHours(OPERATIONS.find((op) => op.group === "WW") as Operation, DEFAULT_BATCH));

/** The lever changes on a group, as words for the summary chips. */
export function changeWords(group: GroupId, levers: Levers): string[] {
	const words: string[] = [];
	if (levers.overtime[group] > 0) words.push(`${levers.overtime[group]}% OT`);
	if (levers.secondShift[group] > 0) words.push(`2nd shift on ${levers.secondShift[group]} ${levers.secondShift[group] > 1 ? groupOf(group).units : groupOf(group).unit}`);
	if (levers.extraMachines[group] > 0) words.push(`+${levers.extraMachines[group]} ${levers.extraMachines[group] > 1 ? groupOf(group).units : groupOf(group).unit}`);
	if (levers.extraPeople[group] > 0) words.push(`+${levers.extraPeople[group]} ${groupOf(group).role}${levers.extraPeople[group] > 1 ? "s" : ""} hired`);
	if (groupOf(group).batchable && levers.batch[group] !== DEFAULT_BATCH) words.push(`batch of ${levers.batch[group]}`);
	levers.moves.filter((m) => m.to === group).forEach((m) => words.push(`${PEOPLE.find((p) => p.id === m.personId)?.name ?? m.personId} moved in`));
	return words;
}

export const groupsChanged = (levers: Levers): GroupId[] => GROUPS.filter((g) => changeWords(g.id, levers).length > 0).map((g) => g.id);
